import React from 'react';
import { useTranslation } from 'react-i18next';
import { ToggleGroup, ToggleGroupItem } from './ui/toggle-group';
import MarkdownContent from './MarkdownContent';

interface MarkdownEditorProps {
  value: string;
  onChange: (value: string) => void;
  /** Section heading rendered next to the Write/Preview toggle. */
  label: string;
  hint?: string;
  placeholder?: string;
  rows?: number;
  id?: string;
}

/**
 * Markdown field with a Write/Preview toggle. Shared by the firmware upload dialog and the release
 * notes editor so a field and its preview are the same everywhere a markdown value is written.
 */
const MarkdownEditor: React.FC<MarkdownEditorProps> = ({ value, onChange, label, hint, placeholder, rows = 8, id }) => {
  const { t } = useTranslation();
  const [tab, setTab] = React.useState<'write' | 'preview'>('write');

  return (
    <section>
      <div className="mb-2 flex items-center justify-between gap-3">
        <h3 className="text-xs font-semibold tracking-wide text-gray-500 uppercase">{label}</h3>

        <ToggleGroup
          type="single"
          size="sm"
          variant="outline"
          value={tab}
          // Radix reports an empty value when the active item is pressed again; keep the tab.
          onValueChange={(next) => next && setTab(next as 'write' | 'preview')}
        >
          <ToggleGroupItem value="write">{t('f-com-write')}</ToggleGroupItem>
          <ToggleGroupItem value="preview">{t('f-com-preview')}</ToggleGroupItem>
        </ToggleGroup>
      </div>

      {tab === 'write' ? (
        <textarea
          id={id}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          rows={rows}
          placeholder={placeholder}
          // box-border: this app loads Tailwind's utilities without preflight, so padding and
          // border would otherwise be added on top of the full width and scroll the dialog.
          className="box-border w-full resize-y rounded-md border border-gray-300 px-3 py-2 font-mono text-sm text-gray-900 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none"
        />
      ) : (
        <div
          className="box-border w-full overflow-x-auto rounded-md border border-gray-200 bg-gray-50 px-3 py-2"
          style={{ minHeight: `${rows * 1.5}rem` }}
        >
          {value.trim() === '' ? (
            <p className="text-sm text-gray-400 italic">{t('f-device-release-notes-empty')}</p>
          ) : (
            <MarkdownContent>{value}</MarkdownContent>
          )}
        </div>
      )}

      {hint && <p className="mt-1 text-xs text-gray-500">{hint}</p>}
    </section>
  );
};

export default MarkdownEditor;
